import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import useDebounce from "src/hooks/useDebounce";
import { CellTextRow, TableSearchResultsItem } from "./types";
import { overlaysRef } from "src/App";
import { setShowTableSearch } from "src/redux/main/mainActions";

const DEBOUNCE_DELAY = 500;

interface UseTableSearchParams {
  cellTextRows: CellTextRow[];
  onScrollToResult: (result: TableSearchResultsItem) => void;
}

export function useTableSearch({
  cellTextRows,
  onScrollToResult,
}: UseTableSearchParams) {
  const [showSearch, setShowSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [highlightedCellIndex, setHighlightedCellIndex] = useState(0);

  const searchValue = useDebounce(inputValue, DEBOUNCE_DELAY);

  const inputRef = useRef<HTMLInputElement>(null);

  const onScrollToResultRef = useRef(onScrollToResult);
  onScrollToResultRef.current = onScrollToResult;

  const { showTableSearch, workspaceListDropdownVisible } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const workspaceListDropdownVisibleRef = useRef(workspaceListDropdownVisible);
  workspaceListDropdownVisibleRef.current = workspaceListDropdownVisible;

  const dispatch = useDispatch();

  // --- Search computation ---

  const searchResults = useMemo<TableSearchResultsItem[]>(() => {
    if (!searchValue || cellTextRows.length === 0) return [];

    const query = searchValue.toLowerCase();
    const results: TableSearchResultsItem[] = [];

    for (let i = 0; i < cellTextRows.length; i++) {
      const row = cellTextRows[i];
      const { values } = row;

      for (let j = 0; j < values.length; j++) {
        const v = values[j];
        if (typeof v === "string" && v.toLowerCase().includes(query)) {
          results.push({ rowIndex: row.index, columnIndex: j });
        }
      }
    }

    return results;
  }, [searchValue, cellTextRows]);

  // Set for O(1) highlight lookups - consumed by table row renderers
  const highlightedCellKeys = useMemo(() => {
    const set = new Set<string>();
    for (const item of searchResults) {
      set.add(`${item.rowIndex}-${item.columnIndex}`);
    }
    return set;
  }, [searchResults]);

  // --- State management ---

  const clearSearch = useCallback(() => {
    setInputValue("");
    setHighlightedCellIndex(0);
  }, []);

  const close = useCallback(() => {
    clearSearch();
    setShowSearch(false);
  }, [clearSearch]);

  const toggle = useCallback(() => {
    setShowSearch((prev) => !prev);
  }, []);

  const open = useCallback(() => {
    setShowSearch(true);
  }, []);

  useEffect(() => {
    if (showTableSearch) {
      setShowSearch(true);
      dispatch(setShowTableSearch(false));
    }
  }, [showTableSearch, dispatch]);

  // --- Navigation ---

  const goToNext = useCallback(() => {
    setHighlightedCellIndex((i) => i + 1);
  }, []);

  const goToPrev = useCallback(() => {
    setHighlightedCellIndex((i) => i - 1);
  }, []);

  // Close if overlays have any children
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const hasNonTooltipElement = Array.from(
        overlaysRef.current?.children ?? [],
      ).some(
        (child): child is HTMLElement =>
          child instanceof HTMLElement &&
          !Array.from(child.classList).some((cls) => cls.includes("tooltip")),
      );

      if (hasNonTooltipElement) {
        close();
      }
    });

    if (overlaysRef.current) {
      observer.observe(overlaysRef.current, {
        childList: true,
      });
    }

    return () => observer.disconnect();
  }, []);

  // Wrap-around index
  useEffect(() => {
    const count = searchResults.length;
    if (count === 0) return;

    if (highlightedCellIndex >= count) {
      setHighlightedCellIndex(0);
    } else if (highlightedCellIndex < 0) {
      setHighlightedCellIndex(count - 1);
    }
  }, [searchResults.length, highlightedCellIndex]);

  // Reset index when search value changes
  useEffect(() => {
    setHighlightedCellIndex(0);
  }, [searchValue]);

  // --- Scroll to active result ---

  useEffect(() => {
    if (searchResults.length === 0) return;

    const cell = searchResults[highlightedCellIndex];
    if (!cell) return;

    onScrollToResultRef.current(cell);
  }, [highlightedCellIndex, searchResults]);

  // --- Keyboard handling ---

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (workspaceListDropdownVisibleRef.current) return;

      if (e.key === "F3" || (e.ctrlKey && e.key === "f")) {
        if (overlaysRef.current.children.length > 0) {
          close();
        } else {
          e.preventDefault();
          toggle();
        }
      }

      if (e.key === "Escape") {
        close();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle, close]);

  // Enter to go to next result (only when search is open)
  useEffect(() => {
    if (!showSearch) return;

    const handleEnter = (e: KeyboardEvent) => {
      if (workspaceListDropdownVisibleRef.current) return;

      if (e.key === "Enter") {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, [showSearch, goToNext]);

  // Focus input when search opens
  useEffect(() => {
    if (showSearch) {
      inputRef.current?.focus();
    } else {
      clearSearch();
    }
  }, [showSearch, clearSearch]);

  // --- Active result for highlight ---

  const activeResult = searchResults[highlightedCellIndex] ?? null;

  return {
    // State
    showSearch,
    inputValue,
    searchValue,
    searchResults,
    highlightedCellIndex,
    highlightedCellKeys,
    activeResult,
    inputRef,

    // Actions
    setInputValue,
    open,
    close,
    toggle,
    goToNext,
    goToPrev,
  };
}

export type UseTableSearchReturn = ReturnType<typeof useTableSearch>;
